<template>
  <div class="page-conformite">
    <landing-header />

    <main>
      <section class="hero-reglementaire">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-3/5">
            <div class="trust-badge">
              <i class="fa-solid fa-shield-check"></i> 100% Conforme Réglementation Fluides
            </div>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              CERFA & Conformité : <br />
              <span class="gradient-text">La rigueur sans la contrainte</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Termoflow ne se contente pas de remplir des formulaires. Nous relions vos documents réglementaires aux flux réels de fluides, à vos équipements et à vos techniciens.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
            </div>
          </div>
          
          <div class="lg:w-2/5 w-full">
            <div class="document-mockup">
              <div class="doc-header">CERFA n° 15497*04</div>
              <div class="doc-lines">
                <div class="line sm"></div>
                <div class="line md"></div>
                <div class="line lg"></div>
              </div>
              <div class="stamp-verified">
                <i class="fa-solid fa-check-double"></i> VALIDE
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center max-w-3xl mx-auto mb-20">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Une gestion reliée aux réalités du terrain</h2>
            <p class="text-gray-600">La conformité est assurée par une correspondance directe entre les documents et vos actifs réels.</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div class="compliance-card">
              <div class="card-icon"><i class="fa-solid fa-wand-magic-sparkles"></i></div>
              <h3>Génération automatisée</h3>
              <p>Données pré-remplies (client, appareil, fluide) directement depuis l'intervention. Zéro ressaisie.</p>
            </div>
            <div class="compliance-card">
              <div class="card-icon"><i class="fa-solid fa-bottle-droplet"></i></div>
              <h3>Suivi des bouteilles</h3>
              <p>Chaque CERFA est rattaché aux bouteilles utilisées : traçabilité totale des prélèvements et récupérations.</p>
            </div>
            <div class="compliance-card">
              <div class="card-icon"><i class="fa-solid fa-mobile-retro"></i></div>
              <h3>Détecteurs étalonnés</h3>
              <p>Intégration auto des informations du détecteur affecté au technicien et de sa date de validité.</p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row gap-16 items-center">
            <div class="lg:w-1/2 space-y-8">
              <div class="step-item">
                <div class="step-number">01</div>
                <div>
                  <h4 class="font-bold text-xl mb-1">Recyclage intégré</h4>
                  <p class="text-gray-600 text-sm">Adresses de récupération et sites de recyclage sont pré-enregistrés et intégrés aux documents.</p>
                </div>
              </div>
              <div class="step-item">
                <div class="step-number">02</div>
                <div>
                  <h4 class="font-bold text-xl mb-1">Signature dématérialisée</h4>
                  <p class="text-gray-600 text-sm">Le client valide sur tablette en fin d'intervention. Le document devient juridiquement exploitable immédiatement.</p>
                </div>
              </div>
              <div class="step-item">
                <div class="step-number">03</div>
                <div>
                  <h4 class="font-bold text-xl mb-1">Archivage intelligent</h4>
                  <p class="text-gray-600 text-sm">Lien automatique entre l'intervention, le technicien et le stock de gaz. Consultable en 1 clic en cas de contrôle.</p>
                </div>
              </div>
              <div class="step-item">
                <div class="step-number">04</div>
                <div>
                  <h4 class="font-bold text-xl mb-1">Export CSV</h4>
                  <p class="text-gray-600 text-sm">Générez des rapports CSV conformes en un clic, facilitant le partage et l'archivage des données.</p>
                </div>
              </div>
            </div>
            
            <div class="lg:w-1/2">
              <div class="audit-box">
                <h3 class="font-bold text-2xl mb-6">Sérénité totale en cas d'audit</h3>
                <p class="mb-6 opacity-90">Grâce à l'automatisation, vous éliminez les erreurs déclaratives et les oublis qui coûtent cher lors des contrôles.</p>
                <div class="audit-list">
                  <div class="audit-check"><i class="fa-solid fa-circle-check"></i> Registre de fluides auto-généré</div>
                  <div class="audit-check"><i class="fa-solid fa-circle-check"></i> Historique 5 ans sécurisé</div>
                  <div class="audit-check"><i class="fa-solid fa-circle-check"></i> Données fiables et cohérentes</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24">
        <div class="max-w-7xl mx-auto px-4 text-center">
          <h2 class="text-3xl font-bold mb-12">Ce que cela change pour vous</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div class="benefit-box">
              <div class="benefit-val">0</div>
              <p>Risque de non-conformité</p>
            </div>
            <div class="benefit-box">
              <div class="benefit-val">-80%</div>
              <p>Temps administratif</p>
            </div>
            <div class="benefit-box">
              <div class="benefit-val">100%</div>
              <p>Traçabilité des fluides</p>
            </div>
            <div class="benefit-box">
              <div class="benefit-val">Zen</div>
              <p>En cas de contrôle</p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 bg-white text-center border-t border-gray-100">
        <div class="max-w-2xl mx-auto px-4">
          <h2 class="text-3xl font-bold text-gray-900 mb-8">Ne laissez plus l'administratif freiner votre croissance</h2>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
             <button class="btn-primary-large">Essayer gratuitement</button>
             <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'ConformitePage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  }
}
</script>

<style scoped>
/* --- TEXT UTILS --- */
.gradient-text {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* --- HERO --- */
.hero-reglementaire {
  padding: 10rem 0 6rem;
  background: radial-gradient(circle at bottom right, #eff6ff, transparent 30%);
}

.trust-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f0fdf4;
  color: #166534;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 800;
  margin-bottom: 2rem;
  border: 1px solid #dcfce7;
}

/* --- BUTTONS --- */
.btn-primary {
  background: linear-gradient(to right, #f97316, #ef4444);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 9999px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(249, 115, 22, 0.2);
}

.btn-ghost {
  padding: 1rem 2.5rem;
  border-radius: 9999px;
  font-weight: 700;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.btn-primary-large {
  background: #f97316;
  color: white;
  padding: 1.2rem 2.5rem;
  border-radius: 9999px;
  font-weight: 800;
  border: none;
}

.btn-outline-large {
  border: 2px solid #e5e7eb;
  padding: 1.2rem 2.5rem;
  border-radius: 9999px;
  font-weight: 800;
  color: #1f2937;
}

/* --- MOCKUP DOCUMENT --- */
.document-mockup {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 2.5rem;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.1);
  position: relative;
  transform: rotate(2deg);
}

.doc-header {
  border-bottom: 2px solid #1f2937;
  font-weight: 900;
  font-size: 0.7rem;
  padding-bottom: 0.5rem;
  margin-bottom: 2rem;
}

.line { background: #f3f4f6; height: 0.6rem; border-radius: 0.2rem; margin-bottom: 0.8rem; }
.line.sm { width: 40%; }
.line.md { width: 80%; }
.line.lg { width: 100%; }

.stamp-verified {
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  border: 3px solid #22c55e;
  color: #22c55e;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 900;
  transform: rotate(-15deg);
  font-size: 1.2rem;
}

/* --- COMPLIANCE CARDS --- */
.compliance-card {
  background: white;
  padding: 2.5rem;
  border-radius: 1.5rem;
  border: 1px solid #f3f4f6;
  transition: all 0.3s ease;
}

.compliance-card:hover { border-color: #f97316; transform: translateY(-5px); }
.card-icon { font-size: 2rem; color: #f97316; margin-bottom: 1.5rem; }
.compliance-card h3 { font-weight: 800; font-size: 1.2rem; margin-bottom: 1rem; }
.compliance-card p { color: #6b7280; font-size: 0.95rem; line-height: 1.6; }

/* --- STEP ITEMS --- */
.step-item { display: flex; gap: 1.5rem; align-items: flex-start; }
.step-number {
  background: #f97316;
  color: white;
  font-weight: 900;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}

/* --- AUDIT BOX --- */
.audit-box {
  background: #1f2937;
  color: white;
  padding: 3.5rem;
  border-radius: 2.5rem;
}

.audit-list { margin-top: 2rem; display: grid; gap: 1rem; }
.audit-check { display: flex; align-items: center; gap: 0.75rem; font-weight: 600; }
.audit-check i { color: #22c55e; }

/* --- BENEFIT BOXES --- */
.benefit-box { padding: 2rem; background: #fafafa; border-radius: 1rem; }
.benefit-val { font-size: 2.5rem; font-weight: 900; color: #f97316; margin-bottom: 0.5rem; }
.benefit-box p { font-weight: 700; color: #4b5563; font-size: 0.9rem; }

@media (max-width: 1024px) {
  .hero-reglementaire { text-align: center; padding-top: 8rem; }
  .hero-reglementaire .flex { flex-direction: column; }
  .document-mockup { margin-top: 4rem; transform: none; max-width: 300px; margin-inline: auto; }
}
</style>